<template>
  <vz-order-container :container="container" can-be-pdf>
  </vz-order-container>

  <vz-confirm-phone-modal
    ref="confirmPhoneModal"
    :title="confirmPhoneTitle"
    :context="!isNewOrder ? 'orderUpdate' : 'orderCreate'"
    :need-redirect="isNewOrder"
    @code-successful="getCreateOrderActions().create"
  />
  <vz-manager-help-modal/>
</template>

<script setup lang="ts">
import {LastOrderDraft, OrderDirectionType, OrderForm} from '~/types/types';
import {PropType} from 'vue';
import {useOrderStore} from '~/stores/order';
import {useBackendStore} from '~/stores/backend';
import {getOrder, getOrderCalculation, orderCreate, v3OrderLegacy} from '~/api/order';
import {defaultOrder, getCurrencyText, priceDataToOrderForm, pricePayload,} from '~/common/objects';
import {useLayoutStore} from '~/stores/personal/layout';
import {LAST_ORDER_DRAFT_KEY, ORDER_CREATED_KEY} from '~/types/order/order';
import {useCityStore} from '~/stores/city';
import {useTerminalStore} from '~/stores/terminal';
import {storeToRefs} from 'pinia';
import {getTradingNetworkAddressList, getTradingNetworkList} from '~/api/trading-network';
import VzIndividualOrder from '~/components/personal/order/individual/vz-individual-order.vue';
import VzNetworkOrder from '~/components/personal/order/vz-network-order.vue';
import VzOrderHeaderBlock from '~/components/personal/order/vz-order-header-block.vue';
import {getDraft} from '~/api/draft';
import VzForm from '~/components/ui-kit/vz-form.vue';
import VzManagerHelpModal from '~/components/modals/vz-manager-help-modal.vue';
import VzConfirmPhoneModal from '~/components/modals/vz-confirm-phone-modal.vue';
import {useTemplateStore} from '~/stores/personal/template';
import {TemplateOrder} from '~/types/order/orderV3';
import {until} from '@vueuse/shared';
import {useDomainCountry, useHeaders} from '~/composables/states';
import {deepCopyObject, setYM} from '~~/common/functions';
import VzMultiOrder from '~/components/personal/order/multi-order/vz-multi-order.vue';
import VzOrderTypeSwitcher from '~/components/personal/order/vz-order-type-switcher.vue';
import VzOrderContainer from '~/components/personal/order-container/vz-order-container.vue';
import {OrderContainer} from '~/types/components/order-container';
import VzOrderIndividualTotal from '~/components/personal/order/individual/vz-order-individual-total.vue';
import VzOrderIndividualToolbar from '~/components/personal/order/individual/vz-order-individual-toolbar.vue';
import VzMultiOrderToolbar from '~/components/personal/order/multi-order/vz-multi-order-toolbar.vue';
import VzOrderIndividualTotalSidebar
  from '~/components/personal/order/individual/vz-order-individual-total-sidebar.vue';
import VzMultiOrderTotal from '~/components/personal/order/multi-order/vz-multi-order-total.vue';
import {useSessionStorage} from '@vueuse/core';
import VzMultiOrderTypeSwitcher from '~/components/personal/order/multi-order/vz-multi-order-type-switcher.vue';
import VzLightOrder from '~/components/personal/order/light-order/vz-light-order.vue';
import VzLightOrderDocumentBlock from '~/components/personal/order/light-order/vz-light-order-document-block.vue';
import {useUserStore} from '~/stores/personal/user';

const emit = defineEmits(['cancel-order'])

const props = defineProps({
  order: {
    type: Object as PropType<OrderForm>,
    default: null
  },
  hideTitle: {
    type: Boolean,
    default: false
  },
  light: {
    type: Boolean,
    default: false
  }
})

const {$showToast, $showDialog, $confirm} = useNuxtApp()
const route = useRoute()
const orderStore = useOrderStore()
const {rclCityList, currentCity} = storeToRefs(useCityStore())
const {terminalList} = storeToRefs(useTerminalStore())
const {defaultTemplate} = storeToRefs(useTemplateStore())
const {setPageloader, setIsOverlayShow} = useLayoutStore()
const {isEditOrder} = storeToRefs(useLayoutStore())
const {backendAvailable} = storeToRefs(useBackendStore())
const {isAuth} = toRefs(useUserStore())
const {getCheckedAccountPhone} = useOrderConfirmPhone()

const lastOrderDraftData = useSessionStorage<LastOrderDraft>(
  LAST_ORDER_DRAFT_KEY,
  null,
  {serializer: {read: JSON.parse, write: JSON.stringify}} // иначе не работает
)

const formRef = ref<typeof VzForm | null>(null)
const headers = useHeaders()

const form = ref(null as OrderForm | null)
const isReadyToDraw = ref(false)
const loading = ref(false)
const isOrderNotFound = ref(false)
const tempOrder = ref({} as OrderForm)

const orderFormComponent = computed(() => {
  if (props.light) {
    return VzLightOrder
  }

  switch (orderStore.orderType) {
    case 'individual':
      return VzIndividualOrder
    case 'networking':
      return VzNetworkOrder
    case 'multi':
      return VzMultiOrder
  }
})

const container = computed((): OrderContainer => {
  const result: OrderContainer = {
    isOrderNotFound: isOrderNotFound.value,
    isReadyToDraw: isReadyToDraw.value,
    toolbar: props.hideTitle ? undefined : VzOrderIndividualToolbar,
    orderSwitcher: VzOrderTypeSwitcher,
    orderHeader: VzOrderHeaderBlock,
    orderForm: orderFormComponent.value,
    orderTotal: VzOrderIndividualTotal,
    orderSidebar: VzOrderIndividualTotalSidebar,
    totalBlockLoading: loading.value,
    onSendForm,
  }

  if (isMulti.value) {
    result.orderTotal = VzMultiOrderTotal
    result.orderSidebar = undefined //VzMultiOrderTotalSidebar
    if (orderStore.multiOrder.selectedRequestId) {
      result.toolbar = VzMultiOrderToolbar
      result.orderSwitcher = VzMultiOrderTypeSwitcher
    }
  }

  if (props.light) {
    result.orderFormFooter = VzLightOrderDocumentBlock
    result.orderSwitcher = undefined
  }

  return result
})

const isNewOrder = computed(() => {
  return !route.params.guid
})

const isPersonal = computed(() => {
  return route.path.startsWith('/personal/') || route.path.startsWith('/old-personal/')
})

const cloneGuid = computed(() => {
  return route.query.clone as string || ''
})

const calculationGuid = computed(() => {
  return route.query.calculation as string || ''
})

const draftGuid = computed(() => {
  return route.query.draft as string || ''
})

const cloneDraftGuid = computed(() => {
  return route.query.cloneDraft as string || ''
})

const templateId = computed(() => {
  return +(route.query.template || 0)
})

const confirmPhoneTitle = computed(() => {
  return !isNewOrder.value ? 'Подтверждение изменений' : 'Подтверждение оформления'
})

const isNetwork = computed(() => {
  return orderStore.orderType === 'networking'
})

const isMulti = computed(() => {
  return orderStore.orderType === 'multi'
})

const linkedOrders = computed(() => {
  return orderStore.orderForm.linkedOrders
})

const networkAddressGuid = computed(() => {
  return orderStore.orderForm.directions.destination.networkGuid
    ? orderStore.orderForm.directions.destination.networkAddressGuid || ''
    : ''
})

const hasAnotherCurrency = computed(() => {
  return Object.keys(orderStore.orderTotalPrice?.currency?.list || {}).length > 1
})

const hasSaturdayDelivery = computed(() => {
  return new Date(orderStore.orderForm.directions.destination.calendar.date)?.getDay() === 6
    && orderStore.orderForm.directions.destination.direction === 'address'
    && orderStore.orderForm.customer.destination.type === 'corporation'
    && isNewOrder.value
})

function orderSaveToStorage() {
  // Если есть сохранённый заказ, то обновляем его. Если только это не завершённая мультизаявка
  if (lastOrderDraftData.value?.order && !orderStore.multiOrder.locked) {
    orderStore.saveToStorage()
  }
}

onBeforeMount(async () => {
  window.addEventListener('visibilitychange', orderSaveToStorage)
  orderStore.isTemplate = false
  isEditOrder.value = true
  orderStore.getPriceRequestCount = 0
  orderStore.lightOrderStep = 0
  await prepareOrder()
})

onBeforeUnmount(() => {
  window.removeEventListener('visibilitychange', orderSaveToStorage)
  orderSaveToStorage()
})

watch(() => orderStore.orderType,
  () => {
    if (orderStore.orderType === 'networking') {
      orderStore.orderForm.directions.destination.direction = 'address'
    }
  }
)

watch(() => defaultTemplate.value,
  () => {
    if (isNewOrder.value && defaultTemplate.value?.id && !cloneGuid.value && !calculationGuid.value &&
      !draftGuid.value && !cloneDraftGuid.value && !templateId.value) {
      orderStore.applyTemplate(defaultTemplate.value as TemplateOrder, true)
    }
  }
)

function getCreateOrderActions():
  { confirmCurrency?: () => Promise<boolean>, confirmPhone?: (confirmed: boolean | null) => Promise<boolean>, createTest: any, create: any } {
  const isMultiOrderRequest = orderStore.multiOrder.selectedRequestId


  if (!isMulti.value) {
    return {
      confirmCurrency: currencyConfirm,
      confirmPhone: checkPhone,
      createTest: onCreateTest,
      create: onCreateOrder
    }
  }

  // Создание заявки в мультизаказе
  if (isMultiOrderRequest) {
    return {
      createTest: async () => {
        await orderStore.multiOrder.saveRequest(orderStore.timetable?.destination)
        return {}
      },
      confirmPhone: async (more: boolean | null) => {
        // Не смотрите на название, если more то открываем форму создания новой заявки
        if (more) {
          orderStore.multiOrder.addRequest()
          await orderStore.getOrderPriceAction()
        }
        return true
      },
      create: () => {
      }
    }
  }

  // Создание мультизаказа
  return {
    confirmCurrency: currencyConfirm,
    confirmPhone: checkPhone,
    createTest: () => orderStore.multiOrder.sendMultiFormTest(route.query.phoneNumber),
    create: (confirmationPhone: string) => orderStore.multiOrder.createMultiOrder(confirmationPhone, route.query.phoneNumber).finally(() => {
      setPageloader(false)
      loading.value = false
      orderStore.orderTotalPriceLoader = false
    })
  }
}

async function prepareCloneOrder() {
  const data = await getOrder({id: cloneGuid.value})

  data.customer.dispatch.sendCode = true
  data.customer.destination.sendCode = true

  if (data.reject) {
    isOrderNotFound.value = true
    return
  }

  orderStore.updateOrderPrice({services: [data], shippingDiagram: data.shippingDiagram, cost: data.cost})
  tempOrder.value = priceDataToOrderForm(data)
  const copyTempOrder = deepCopyObject(tempOrder.value)
  tempOrder.value.linkedOrders = null
  tempOrder.value.clonedGuid = cloneGuid.value
  tempOrder.value.meta = {
    isNetwork: tempOrder.value.meta.isNetwork,
    checkCargoBeforeShipping: tempOrder.value.meta.checkCargoBeforeShipping,
    additionInfo: tempOrder.value.meta.additionInfo,
  }
  tempOrder.value.depositBonusFromDiscount = copyTempOrder.totalPrice?.savedBonusFromDiscount || 0
  const mode = tempOrder.value.cargo.mode
  if (mode !== 'pallets') {
    tempOrder.value.cargo.pallets = defaultOrder.cargo.pallets
  }
  if (mode !== 'parcels') {
    tempOrder.value.cargo.parcels = defaultOrder.cargo.parcels
  }
  if (mode !== 'places') {
    tempOrder.value.cargo.places = defaultOrder.cargo.places
  }
}

async function prepareCalculation() {
  const data = await getOrderCalculation(calculationGuid.value)

  if (!data) {
    isOrderNotFound.value = true
    return
  }
  orderStore.updateOrderPrice({services: [data], shippingDiagram: data.shippingDiagram, cost: data.cost})
  tempOrder.value = priceDataToOrderForm(data)
}

async function prepareDraftOrder() {
  const id = (draftGuid.value || route.query.cloneDraft) as string
  const data = await getDraft(id)
  if (data?.error) {
    $showToast({
      title: 'Ошибка',
      variant: 'error',
      text: data.error?.message,
      autocloseDelay: 7000
    })
    isOrderNotFound.value = true
    return
  }
  // orderStore.orderTotalPrice = data.cost
  tempOrder.value = priceDataToOrderForm(data)
  tempOrder.value.linkedOrders = null
  if (draftGuid.value) {
    tempOrder.value.meta.draftId = id
  }
}

async function prepareOrder(): Promise<void> {
  loading.value = true
  setPageloader(true)
  tempOrder.value = props.order

  if (cloneGuid.value) {
    await prepareCloneOrder()
  }
  if (draftGuid.value || route.query.cloneDraft) {
    await prepareDraftOrder()
  }
  if (calculationGuid.value) {
    await prepareCalculation()
  }

  if (!tempOrder.value) {
    await prepareNewOrder()
  } else {
    await prepareEditOrder()
    isReadyToDraw.value = true
  }

  if (templateId.value) {
    await orderStore.applyTemplate({id: templateId.value}, true)
  }

  setPageloader(false)
  loading.value = false
}

async function checkPhone(confirmed: boolean | null): Promise<boolean> {
  const accountPhone = await getCheckedAccountPhone()
  if (!confirmed && !accountPhone) {
    $showDialog({name: 'confirm-phone'})
    if (isPersonal.value) {
      $showToast({
        title: 'Добавьте телефон в настройки',
        text: 'Уважаемый клиент! Напоминаем Вам, что, указав телефон в <a href="/personal/account/" target="_blank">' +
          'настройках личного кабинета</a>, Вы сможете оформлять заказы без подтверждения SMS.',
        variant: 'info',
        autocloseDelay: 12000
      })
    }
    setPageloader(false)
    loading.value = false
    orderStore.orderTotalPriceLoader = false
    return false
  }
  return true
}

async function onSendForm(confirmed = null) {
  const createActions = getCreateOrderActions()

  if (!(await saturdayDeliveryConfirm())) {
    return
  }

  if (createActions.confirmCurrency && !(await createActions.confirmCurrency())) {
    return
  }

  // При отправке формы всё блокируем
  setPageloader(true)
  loading.value = true
  orderStore.orderTotalPriceLoader = true
  orderStore.orderForm.context = orderStore.appliedTemplates.length
    ? {requestSource: 'template', requestVersion: 'v1', requestId: orderStore.appliedTemplates}
    : undefined
  if (props.light) {
    orderStore.orderForm.context = {
      ...(orderStore.orderForm.context || {}),
      lightOrder: true
    }
  }


  // Делаем тестовый запрос на создание заказа
  const res = await createActions.createTest()

  if (res.reject) {
    setPageloader(false)
    loading.value = false
    orderStore.orderTotalPriceLoader = false
    return
  }
  // Если есть предупреждения, выводим их, но если нет ошибок, то заказ можно создавать (res будет пустой)
  if (res.warnings) {
    await orderStore.setOrderWarnings(res.warnings)
    delete res.warnings
  }
  // Если нет ошибок и телефон проверен, то создаём заказ
  if (Object.keys(res).length === 0 && (!createActions.confirmPhone || await createActions.confirmPhone(confirmed))) {
    await createActions.create('')
  }
  setPageloader(false)
  orderStore.orderTotalPriceLoader = false
  loading.value = false
}

async function onCreateTest() {
  // if(!isAuth.value) {
  //   return v3OrderLegacy({...pricePayload(orderStore.orderForm), phone: route.query.phoneNumber}, true)
  // }

  return orderCreate({...pricePayload(orderStore.orderForm), phone: route.query.phoneNumber}, true)
}

async function onCreateOrder(confirmationPhone: string, confirmCode?: string) {
  setPageloader(true)
  loading.value = true
  orderStore.orderTotalPriceLoader = true
  const query: any = {}
  const form = pricePayload(orderStore.orderForm)
  const link = isPersonal.value ? '/personal/order' : '/order/administer'
  if (isNewOrder.value && draftGuid.value) {
    form.draftId = draftGuid.value
  }

  let result
  if(confirmCode) {
    try {
      result = await v3OrderLegacy({
        ...form,
        confirmCode,
        confirmedCreateUserPhone: confirmationPhone,
        phone: route.query.phoneNumber
      })
    } catch (e) {
      await checkPhone(null)
      return
    }
  } else {
    result = await orderCreate({
      ...form,
      confirmedCreateUserPhone: confirmationPhone,
      phone: route.query.phoneNumber
    })
  }

  const {id, reject} = result
  setPageloader(false)
  loading.value = false
  orderStore.orderTotalPriceLoader = false
  setIsOverlayShow(false)

  if (reject) {
    return
  }
  query.phoneNumber = confirmationPhone || route.query.phoneNumber

  if (isNewOrder.value && id) {
    setYM(38058485, 'oformlen')
    if (!isPersonal.value) {
      setYM(38058485, 'registracia')
    }
    // флаг возможности оставления отзыва
    sessionStorage.setItem(ORDER_CREATED_KEY, id)
    // order-sent - флаг для seo
    query['order-sent'] = '1'
    lastOrderDraftData.value = null
    await useRouter().push({path: `${link}/${id}/`, query})
  } else {
    window.location.reload()
  }
}

async function prepareNewOrder() {
  await orderStore.setEmptyOrder()
  await until(() => !!rclCityList.value.length).toBe(true)
  const query = route.query

  const isNetworkingOrder = query.is_networking === 'true';
  const locationDispatch = (query.location_dispatch || currentCity.value?.guid || rclCityList.value[0]?.guid) as string
  let locationDestination = (query.location_destination || rclCityList.value[0]?.guid) as string
  if (locationDispatch === locationDestination && locationDestination === rclCityList.value[0]?.guid) {
    if (!query.location_dispatch || query.location_dispatch !== query.location_destination) {
      locationDestination = rclCityList.value[1]?.guid
    }
  }
  const terminalDispatch = query.dispatch_point === 'terminal'
    ? (query.dispatch_terminal
        ? query.dispatch_terminal.toString()
        : terminalList.value.find(t => t.location_guid === locationDispatch)?.guid || ''
    )
    : ''
  const terminalDestination = query.destination_point === 'terminal'
    ? (query.destination_terminal
        ? query.destination_terminal.toString()
        : terminalList.value.find(t => t.location_guid === locationDestination)?.guid || ''
    )
    : ''

  orderStore.orderForm.directions.dispatch.locationGuid = locationDispatch
  orderStore.orderForm.directions.destination.locationGuid = locationDestination

  if (query.dispatch_point) {
    orderStore.orderForm.directions.dispatch.direction = query.dispatch_point as OrderDirectionType
    if (query.dispatch_point === 'terminal') {
      orderStore.orderForm.directions.dispatch.terminalGuid = terminalDispatch
    }
  }

  if (query.destination_point) {
    orderStore.orderForm.directions.destination.direction = query.destination_point as OrderDirectionType
    if (query.destination_point === 'terminal') {
      orderStore.orderForm.directions.destination.terminalGuid = terminalDestination
    }
  }

  if (query.dispatch_address) {
    orderStore.orderForm.directions.dispatch.address = query.dispatch_address as string
  }

  if (query.destination_pickup_point) {
    orderStore.orderForm.directions.destination.pickupPointGuid = query.destination_pickup_point as string
  }

  if (query.category) {
    orderStore.orderForm.cargo.category = query.category as string
  }

  if (isNetworkingOrder) {
    const networkAddressGuid = query.network_address_guid as string
    if (networkAddressGuid) {
      orderStore.orderForm.directions.destination.networkAddressGuid = networkAddressGuid
    }
    if (query.tax_id) {
      const data = await getTradingNetworkList({taxId: query.tax_id.toString()})
      if (data[0].guid) {
        orderStore.orderForm.customer.destination.corporation.taxId = query.tax_id as string
        orderStore.orderForm.directions.destination.networkGuid = data[0].guid
        orderStore.orderForm.customer.destination.type = 'corporation'
        orderStore.orderForm.customer.destination.networking = true

        const networkAddressList = await getTradingNetworkAddressList({networkGuid: data[0].guid, search: ''})
        const networkAddress = networkAddressList.find(i => i.guid === networkAddressGuid)
        if (networkAddress?.address) {
          orderStore.orderForm.directions.destination.address = networkAddress.address
        }
      }
    }
  }
  await orderStore.setOrderType(isNetworkingOrder ? 'networking' : 'individual')

  if (query.volume) {
    orderStore.orderForm.cargo.dimensions.general.volume = Number(query.volume)
  }
  if (query.weight) {
    orderStore.orderForm.cargo.dimensions.general.weight = Number(query.weight)
    orderStore.orderForm.cargo.dimensions.detail.weight = Number(query.weight)
  }
  if (query.currency) {
    orderStore.orderForm.currency = query.currency as string
  }
  if (query.promo) {
    orderStore.orderForm.promocode = query.promo as string
  }

  if (defaultTemplate.value?.id && !cloneGuid.value && !calculationGuid.value && !draftGuid.value && !cloneDraftGuid.value && !templateId.value) {
    orderStore.applyTemplate(defaultTemplate.value as TemplateOrder, true)
  }

  // если валюта отлична от RU и BY, ставим по умолчанию значение 'Без страхования'
  if (!['RU', 'BY'].includes(orderStore.orderForm.currency)) {
    orderStore.orderForm.cargo.insurance.type = 'without'
  }

  setTimeout(() => {
    orderStore.orderForm.customer.dispatch.country = orderStore.dispatchLocation?.country || useDomainCountry()
    orderStore.orderForm.customer.destination.country = orderStore.destinationLocation?.country || useDomainCountry()
  }, 100)

  // TODO: если стоит одинаковый город и указан терминал, то при нажатии на расчитать(с калькулятора главной страницы) ничего не происходит
  // и при обновлении страницы с квери тоже, можно сделать isReadyToDraw.value = true
  // сказали что поправят в 1c Все из-за timetable в destination не приходит dates
  // isReadyToDraw.value = true
  continueOldOrderToast()
}

async function prepareEditOrder(): Promise<void> {
  if (route.query.clone) {
    // Если нет терминала в списке
    if (tempOrder.value.directions.dispatch.terminalGuid && !terminalList.value?.find(
      t => t.guid === tempOrder.value.directions.dispatch.terminalGuid)
    ) {
      tempOrder.value.directions.dispatch.terminalGuid = terminalList.value?.find(
        t => t.location_guid === tempOrder.value.directions.dispatch.locationGuid
      )?.guid || ''
    }
    if (tempOrder.value.directions.destination.terminalGuid && !terminalList.value.find(
      t => t.guid === tempOrder.value.directions.destination.terminalGuid)
    ) {
      tempOrder.value.directions.destination.terminalGuid = terminalList.value?.find(
        t => t.location_guid === tempOrder.value.directions.destination.locationGuid
      )?.guid || ''
    }
  }

  const services: any = {}
  orderStore.orderForm.services?.forEach((service) => {
    services[service.guid] = service.payer
  })
  await orderStore.setOrderForm(tempOrder.value)
  orderStore.orderForm.customer.segregated.list = services
  await orderStore.setOrderType(tempOrder.value.meta?.isNetwork ? 'networking' : 'individual')
  if (tempOrder.value.meta?.isNetwork) {
    // костыль, иначе адресс сбрасывается
    orderStore.orderForm.directions.destination.address = tempOrder.value.directions.destination.address
  }
}

async function currencyConfirm() {
  if (!hasAnotherCurrency.value) {
    return true
  }
  if (orderStore.orderForm.currency === orderStore.orderForm.defaultCurrency) {
    return true
  }
  const currentCurrencyText = getCurrencyText(orderStore.orderForm.currency).replace('руб', 'рубли')
  const modalText = `Выбранная валюта оплаты: ${currentCurrencyText}. <b>Внимание!</b> Сменить валюту после оформления будет <u>невозможно</u>.`

  return !!(await $confirm({
    title: 'Подтверждение валюты',
    text: modalText,
    cancelText: 'Хочу изменить валюту',
    buttonText: 'Подтвердить'
  }))
}

async function saturdayDeliveryConfirm() {
  if (!hasSaturdayDelivery.value) {
    return true
  }

  return !!(await $confirm({
    title: 'Доставка в субботу',
    text: 'Дата доставки выпадает на субботу. Подходит ли вам доставка в этот день?',
    cancelText: 'Изменить дату',
    buttonText: 'Подтвердить'
  }))
}

function continueOldOrderToast() {
  if (lastOrderDraftData.value?.order && !templateId.value && isAuth.value && backendAvailable.value) {
    // const title = orderTypes.find(type => type.value === lastOrderDraftData.value?.type)?.title

    $showToast({
      title: 'Возобновить сеанс?',
      text: 'В прошлый раз Вы не закончили оформление заказа. Желаете продолжить?',
      variant: 'info',
      buttonText: 'Продолжить',
      buttonCB: () => {
        if (lastOrderDraftData.value.list?.length) {
          orderStore.multiOrder.getFromStorage()
          orderStore.skipDefaultTimeFlag = true
        }
        orderStore.orderType = lastOrderDraftData.value.type || orderStore.orderType
        orderStore.orderForm = deepCopyObject(lastOrderDraftData.value.order)
        // Сохраняем каждое изменение
        orderStore.getPriceRequestCount = 1000
      },
      cancelButtonText: 'Не нужно',
      cancelCB: () => {
        lastOrderDraftData.value = null
      },
      autocloseDelay: 15000,
    })

    // При первом заходе спрашиваем продолжить ли со старой формой
  }
}

watch(
  () => orderStore.timetable,
  () => {
    if (!isReadyToDraw.value) {
      isReadyToDraw.value = true
    }
  }
)
</script>
